import React from 'react'
import AttachmentPreview from './AttachmentPreview'
import { useAttachments } from '../../hooks/useAttachments'

interface MessageFormProps {
  newMessage: string
  setNewMessage: (message: string) => void
  onSendMessage: (e: React.FormEvent, attachments: File[]) => void
  loading: boolean
}

const MessageForm = ({ newMessage, setNewMessage, onSendMessage, loading }: MessageFormProps) => {
  const {
    attachments,
    showAttachments,
    handleAttachmentChange,
    handleRemoveAttachment,
    fileInputRef,
    resetAttachments
  } = useAttachments()

  const handleAttachmentClick = () => {
    fileInputRef.current?.click()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim() && attachments.length === 0) {
      return
    }
    onSendMessage(e, attachments)

    resetAttachments()
  }

  return (
    <div
      className='tw-border-t tw-border-slate-200 dark:tw-border-slate-700 tw-bg-slate-50 dark:tw-bg-slate-800 tw-p-2.5 md:tw-p-3'
    >
      {showAttachments && attachments.length > 0 && (
        <AttachmentPreview
          attachments={attachments}
          onRemoveAttachment={handleRemoveAttachment}
        />
      )}

      <form onSubmit={handleSubmit} className='tw-flex tw-items-center tw-gap-x-2'>
        <button
          type='button'
          className='tw-flex-shrink-0 tw-p-2.5 tw-text-slate-500 dark:tw-text-slate-400 hover:tw-text-sky-600 dark:hover:tw-text-sky-400 hover:tw-bg-slate-200 dark:hover:tw-bg-slate-700 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-sky-500 transition-colors'
          onClick={handleAttachmentClick}
          title='Dodaj załącznik'
          disabled={loading}
          aria-label='Dodaj załącznik'
        >
          <i className='fa fa-paperclip fa-fw' />
        </button>
        <input
          type='file'
          ref={fileInputRef}
          className='tw-hidden'
          onChange={handleAttachmentChange}
          multiple
          disabled={loading}
          accept='image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar'
        />

        <input
          type='text'
          className='tw-flex-grow tw-bg-white dark:tw-bg-slate-700/50 tw-text-slate-800 dark:tw-text-slate-100 tw-border tw-border-slate-300 dark:tw-border-slate-600 tw-rounded-lg tw-px-3.5 tw-py-2 focus:tw-ring-2 focus:tw-ring-sky-500 focus:tw-border-transparent focus:tw-outline-none tw-placeholder-slate-400 dark:tw-placeholder-slate-500 tw-text-sm transition-colors'
          placeholder='Wpisz wiadomość...'
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          disabled={loading}
        />

        <button
          type='submit'
          className='tw-flex-shrink-0 tw-bg-[#86B97C] hover:tw-bg-[#70A864] tw-text-white tw-p-2.5 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-1 focus:tw-ring-[#86B97C] disabled:tw-opacity-60 disabled:tw-cursor-not-allowed transition-colors'
          disabled={loading || (!newMessage.trim() && attachments.length === 0)}
          aria-label='Wyślij wiadomość'
        >
          {loading
            ? (
              <svg className='tw-animate-spin tw-h-5 tw-w-5 tw-text-white' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                <circle className='tw-opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                <path className='tw-opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
              </svg>
              )
            : (
              <i className='fa fa-paper-plane fa-fw' />
              )}
        </button>
      </form>
    </div>
  )
}

export default MessageForm
